import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  SignalCellularAlt as SignalIcon,
  Timer as TimerIcon,
  Error as ErrorIcon,
  People as PeopleIcon,
  PhoneCallback as InboundIcon,
  PhoneForwarded as OutboundIcon,
} from '@mui/icons-material';
import { monitoringAPI } from '../services/api';

function StackedMetricCard({ title, values, icon: Icon }) {
  const getColor = (type) => {
    switch(type) {
      case 'agents': return 'primary.main';
      case 'inbound': return 'info.main';
      case 'outbound': return 'success.main';
      default: return 'text.primary';
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'agents': return <PeopleIcon sx={{ fontSize: 16 }} />;
      case 'inbound': return <InboundIcon sx={{ fontSize: 16, transform: 'rotate(180deg)' }} />;
      case 'outbound': return <OutboundIcon sx={{ fontSize: 16 }} />;
      default: return null;
    }
  };

  const getLabel = (type) => {
    switch(type) {
      case 'agents': return 'Agent';
      case 'inbound': return 'Inbound';
      case 'outbound': return 'Outbound';
      default: return type;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Icon sx={{ color: 'text.secondary', mr: 1 }} />
          <Typography variant="subtitle1" component="div" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {Object.entries(values).map(([type, value], index) => (
            <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getIcon(type)}
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                  {getLabel(type)}
                </Typography>
              </Box>
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ color: getColor(type), fontWeight: 'bold' }}
              >
                {value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

function Wallboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [timeRange, setTimeRange] = useState(15);

  const fetchData = async () => {
    try {
      const response = await monitoringAPI.getWallboard(timeRange);
      console.log('Wallboard API response:', response.data);
      console.log('metrics_by_type:', response.data?.metrics_by_type);
      setData(response.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load wallboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, [timeRange]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const metricsByType = data?.metrics_by_type || {};
  const failures = data?.failure_breakdown || [];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Wallboard - Real-time Metrics
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="time-range-label">Time Range</InputLabel>
            <Select
              labelId="time-range-label"
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value={1}>1 minute</MenuItem>
              <MenuItem value={5}>5 minutes</MenuItem>
              <MenuItem value={10}>10 minutes</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>60 minutes</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Last updated: {lastUpdate.toLocaleTimeString()} (refreshing every 15s)
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Active Calls and Active Agents - Combined */}
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
                {/* Active Calls */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Active Calls
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ my: 1, color: 'primary.main' }}>
                    {data?.active_calls || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current concurrent calls
                  </Typography>
                </Box>

                {/* Active Agents */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PeopleIcon sx={{ color: 'text.secondary', mr: 1 }} />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Active Agents
                    </Typography>
                  </Box>
                  <Typography variant="h3" component="div" sx={{ my: 1, color: 'secondary.main' }}>
                    {data?.active_agents || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current agent calls
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Calls - Stacked by type */}
        <Grid item xs={12} sm={6} md={4}>
          <StackedMetricCard
            title={`Total Calls (${timeRange}m)`}
            icon={TrendingUpIcon}
            values={{
              agents: metricsByType.agents?.total_calls || 0,
              inbound: metricsByType.inbound?.total_calls || 0,
              outbound: metricsByType.outbound?.total_calls || 0,
            }}
          />
        </Grid>

        {/* CPS - Stacked by type */}
        <Grid item xs={12} sm={6} md={4}>
          <StackedMetricCard
            title="CPS"
            icon={SignalIcon}
            values={{
              agents: (metricsByType.agents?.cps || 0).toFixed(3),
              inbound: (metricsByType.inbound?.cps || 0).toFixed(3),
              outbound: (metricsByType.outbound?.cps || 0).toFixed(3),
            }}
          />
        </Grid>

        {/* ASR - Stacked by type */}
        <Grid item xs={12} sm={6} md={3}>
          <StackedMetricCard
            title="ASR %"
            icon={TrendingUpIcon}
            values={{
              agents: `${(metricsByType.agents?.asr || 0).toFixed(1)}%`,
              inbound: `${(metricsByType.inbound?.asr || 0).toFixed(1)}%`,
              outbound: `${(metricsByType.outbound?.asr || 0).toFixed(1)}%`,
            }}
          />
        </Grid>

        {/* ACD - Stacked by type */}
        <Grid item xs={12} sm={6} md={3}>
          <StackedMetricCard
            title="ACD (sec)"
            icon={TimerIcon}
            values={{
              agents: metricsByType.agents?.acd || 0,
              inbound: metricsByType.inbound?.acd || 0,
              outbound: metricsByType.outbound?.acd || 0,
            }}
          />
        </Grid>

        {/* MOS - Stacked by type */}
        <Grid item xs={12} sm={6} md={3}>
          <StackedMetricCard
            title="Avg MOS"
            icon={SignalIcon}
            values={{
              agents: metricsByType.agents?.avg_mos?.toFixed(2) || 'N/A',
              inbound: metricsByType.inbound?.avg_mos?.toFixed(2) || 'N/A',
              outbound: metricsByType.outbound?.avg_mos?.toFixed(2) || 'N/A',
            }}
          />
        </Grid>

        {/* Total Minutes - Stacked by type */}
        <Grid item xs={12} sm={6} md={3}>
          <StackedMetricCard
            title="Total Minutes"
            icon={TimerIcon}
            values={{
              agents: Math.round(metricsByType.agents?.total_minutes || 0),
              inbound: Math.round(metricsByType.inbound?.total_minutes || 0),
              outbound: Math.round(metricsByType.outbound?.total_minutes || 0),
            }}
          />
        </Grid>

        {/* Failure Breakdown */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
              <Typography variant="h6">Top Failure Causes</Typography>
            </Box>
            {failures.length > 0 ? (
              <Grid container spacing={2}>
                {failures.slice(0, 5).map((failure, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {failure.cause}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h6" color="error">
                          {failure.count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ({failure.ratio}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No failures in the last {timeRange} minutes
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Wallboard;
